import React, { useState } from 'react';
import { HelpCircle, Plus, Minus, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from './components/Header6';
import Footer from './components/Footer6';

const FAQs6 = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [openIndex, setOpenIndex] = useState(null);

    const categories = [
        {
            label: 'Orders & Shipping',
            questions: [
                { question: 'How long does delivery take?', answer: 'Standard shipping within India takes 5-7 business days. Express shipping delivers within 2-3 business days. For handloom pieces that are made-to-order, please allow an additional 7-14 business days for weaving before dispatch.' },
                { question: 'Do you deliver to my pincode?', answer: 'We deliver to over 25,000 pincodes across India through our logistics partners. You can check delivery availability by entering your pincode on the product page. International shipping is available to select countries.' },
                { question: 'How can I track my order?', answer: 'Once your order is dispatched, you will receive a shipping confirmation email and SMS with a tracking link. You can also track your order by logging into your account and visiting the "My Orders" section.' },
                { question: 'Is Cash on Delivery available?', answer: 'Yes, Cash on Delivery (COD) is available for orders up to \u20B910,000 at select pincodes. A nominal COD handling fee of \u20B949 applies. For higher value orders, we recommend online payment for a smoother experience.' },
                { question: 'What if I am not available to receive my order?', answer: 'Our delivery partner will make up to 3 delivery attempts. After 3 failed attempts, the package will be returned to our warehouse and we will contact you to arrange reshipment.' },
            ]
        },
        {
            label: 'Returns & Exchange',
            questions: [
                { question: 'What is your exchange policy?', answer: 'We accept exchange requests within 7 days of delivery. Items must be unused, unwashed, and in their original condition with all tags intact. Handloom products may have slight natural variations which are not considered defects.' },
                { question: 'How do I initiate an exchange?', answer: 'Log into your account, navigate to "My Orders", and click "Request Exchange". Alternatively, contact us via WhatsApp at +91 98765 43210 with your order number and reason for exchange.' },
                { question: 'Can I get a refund?', answer: 'Yes, refunds are available for eligible items. Once we receive and inspect the returned product, the refund is processed within 5-7 business days to your original payment method. COD refunds are processed via bank transfer.' },
                { question: 'Are custom orders eligible for return?', answer: 'Custom-made and bespoke handloom pieces are not eligible for return or exchange unless there is a manufacturing defect. We recommend reviewing all details carefully before placing custom orders.' },
            ]
        },
        {
            label: 'Products & Care',
            questions: [
                { question: 'Are your products genuinely handloom?', answer: 'Yes, every product carries a Handloom Mark or GI Tag certifying its authenticity. We work directly with artisan clusters and each piece comes with an artisan story card detailing the weaver and the technique used.' },
                { question: 'How should I care for handloom fabrics?', answer: 'For the first wash, we recommend dry cleaning. Subsequently, gentle hand wash in cold water with mild detergent is ideal. Do not wring. Dry in shade. Iron on medium heat. Handloom fabrics become softer and more beautiful with use.' },
                { question: 'Why do handloom products have slight variations?', answer: 'Handloom weaving is done entirely by hand. Slight variations in colour, texture, and pattern are natural characteristics that distinguish genuine handloom from machine-made fabrics. These are marks of authenticity, not defects.' },
                { question: 'Do you offer custom sizing?', answer: 'Yes, we offer custom sizing for most kurtas, sets, and blouses. Select "Custom Size" on the product page and enter your measurements. Custom orders take an additional 7-14 business days.' },
            ]
        },
        {
            label: 'Payments & Offers',
            questions: [
                { question: 'What payment methods do you accept?', answer: 'We accept Credit/Debit Cards (Visa, Mastercard, RuPay), UPI (Google Pay, PhonePe, Paytm), Net Banking, and Cash on Delivery. All online payments are processed securely through Razorpay.' },
                { question: 'Do you offer EMI options?', answer: 'Yes, no-cost EMI is available on orders above \u20B93,000 for select bank cards. EMI options will be displayed at checkout based on your card eligibility.' },
                { question: 'How can I apply a coupon code?', answer: 'Enter your coupon code in the "Apply Coupon" field during checkout. Only one coupon can be applied per order. Coupons cannot be combined with other ongoing offers unless stated otherwise.' },
            ]
        },
    ];

    const currentFAQs = categories[activeTab].questions;

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+Display:wght@400;600;700&family=Nunito:wght@300;400;600&display=swap');
            `}</style>
            <div style={{ fontFamily: "'Nunito', -apple-system, sans-serif" }} className="min-h-screen bg-[#F5F0E8]">
                <Header />

                <section className="pt-[120px] pb-12 lg:pb-16" style={{ borderBottom: '1px solid #E8DFD1' }}>
                    <div className="max-w-3xl mx-auto text-center px-5">
                        <p className="text-xs font-semibold tracking-[3px] uppercase mb-4" style={{ color: '#8B4513' }}>Help Centre</p>
                        <h1 style={{ fontFamily: "'Noto Serif Display', serif" }} className="text-4xl lg:text-5xl font-normal text-[#2C1810] mb-4">
                            Frequently Asked Questions
                        </h1>
                        <p className="text-[#7C6B5D] text-base" style={{ fontWeight: 300 }}>Find answers to common questions about our handloom products, shipping, and policies</p>
                    </div>
                </section>

                <div className="max-w-[900px] mx-auto px-5 sm:px-8 py-12 lg:py-16">
                    {/* Tabs */}
                    <div className="flex flex-wrap gap-2 mb-10">
                        {categories.map((cat, i) => (
                            <button
                                key={i}
                                onClick={() => { setActiveTab(i); setOpenIndex(null); }}
                                style={{
                                    padding: '10px 20px', borderRadius: '2px', fontSize: '13px', fontWeight: 600,
                                    letterSpacing: '0.5px', cursor: 'pointer', transition: 'all 0.3s', fontFamily: "'Nunito', sans-serif",
                                    backgroundColor: activeTab === i ? '#8B4513' : 'transparent',
                                    color: activeTab === i ? '#FFFFFF' : '#7C6B5D',
                                    border: activeTab === i ? '1px solid #8B4513' : '1px solid #D4C5B0',
                                }}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>

                    {/* FAQ Items */}
                    <div className="space-y-0">
                        {currentFAQs.map((faq, i) => (
                            <div key={i} style={{ borderBottom: '1px solid #E8DFD1' }}>
                                <button
                                    onClick={() => setOpenIndex(openIndex === i ? null : i)}
                                    style={{
                                        width: '100%', padding: '20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                                    }}
                                >
                                    <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: '15px', fontWeight: 600, color: '#2C1810', paddingRight: '16px' }}>{faq.question}</span>
                                    {openIndex === i ? <Minus size={18} style={{ color: '#8B4513', flexShrink: 0 }} /> : <Plus size={18} style={{ color: '#7C6B5D', flexShrink: 0 }} />}
                                </button>
                                {openIndex === i && (
                                    <p style={{ fontSize: '14px', color: '#7C6B5D', lineHeight: 1.8, paddingBottom: '20px', fontWeight: 300 }}>{faq.answer}</p>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Still Need Help */}
                    <div className="mt-16 text-center" style={{ backgroundColor: '#FAF7F2', border: '1px solid #E8DFD1', borderRadius: '4px', padding: '40px' }}>
                        <MessageCircle size={32} style={{ color: '#8B4513', margin: '0 auto 16px' }} />
                        <h3 style={{ fontFamily: "'Noto Serif Display', serif", fontSize: '22px', fontWeight: 600, color: '#2C1810', marginBottom: '8px' }}>Still Have Questions?</h3>
                        <p style={{ fontSize: '14px', color: '#7C6B5D', marginBottom: '24px', fontWeight: 300 }}>Our team is here to help. Reach out and we will respond within 24 hours.</p>
                        <button onClick={() => navigate('/template6/contact')} style={{ padding: '14px 32px', backgroundColor: '#8B4513', color: '#FFFFFF', border: 'none', borderRadius: '2px', fontSize: '13px', fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Nunito', sans-serif" }}>
                            Contact Us
                        </button>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default FAQs6;
